"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/************************************** THIRD-PARTY MODULES ***************************************/
var util_1 = require("util");
var store_1 = require("../data/store/store");
var javelin_exports_1 = require("../javelin-exports");
/******************************************** LOGGING *********************************************/
var shared_1 = require("mad-logs/lib/shared");
var log = shared_1.logFactory('javelin-json.ts', shared_1.Styles.cult, util_1.inspect);
/********************************************* UTILS **********************************************/
var keys = Object.keys, assign = Object.assign;
/**
 * TODO make recursiveHashMapStringify cleaner
 *
 * Recursively stringify Kotlin HashMap & related types (with getObject functions).
 * @param {Object|any} obj Object (potentially Kotlin HashMap) to convert to plain JS object
 * @return {Object|any} obj converted to JSON-friendly format (plain JS object, or primitive)
 */
var recursiveHashMapStringify = function (obj) {
    log.silly("recursiveHashMapStringify :: obj:", obj);
    // 1. If obj has getObject function, run it and return the result
    if (obj && obj.getObject && typeof obj.getObject === 'function') {
        var cleanObj_1 = obj.getObject();
        return keys(cleanObj_1).reduce(function (acc, key) {
            var _a;
            return assign(acc, (_a = {}, _a[key] = recursiveHashMapStringify(cleanObj_1[key]), _a));
        }, {});
        // 2. If obj is LocalDate or ZonedDateTime instance, convert to timestamp string for display
    }
    else if (obj instanceof javelin_exports_1.ZonedDateTime || obj instanceof javelin_exports_1.LocalDate) {
        return obj.toISOString();
        // 3. If obj is array, return result of running recursiveHashMapStringify on each item in it
    }
    else if (Array.isArray(obj)) {
        var arr = [];
        for (var _i = 0, obj_1 = obj; _i < obj_1.length; _i++) {
            var arrVal = obj_1[_i];
            arr.push(recursiveHashMapStringify(arrVal));
        }
        return arr;
        // 4. If obj isn't an object, just return it
    }
    else if (typeof obj !== 'object' ||
        obj instanceof Number ||
        obj instanceof Boolean ||
        obj instanceof String ||
        obj === null) {
        return obj;
        // 5. If obj is non-array object, check obj[key] for object w/ getObject function, then
        //       run recursiveHashMapStringify on the object at that key if it's found.
        //       Also convert each hashed key to a regular key (e.g. doses_u3i4j3$_0 -> doses)
    }
    else {
        return Object.keys(obj).reduce(function (acc, key) {
            var _a, _b, _c, _d, _e;
            // 5.a. Convert hashed key (e.g. doses_u3i4j3$_0) to regular key (e.g. doses)
            var matchHashedKey = key.match(/.+(?=_[a-z0-9]{6}\$_?0?)/g);
            var cleanKey = matchHashedKey ? matchHashedKey[0] : key;
            // 5.b. If obj[cleanKey] is an array, run recursiveHashMapStringify on
            //      each item in obj, and return
            if (obj[cleanKey] && Array.isArray(obj[cleanKey])) {
                var arr = [];
                for (var _i = 0, _f = obj[cleanKey]; _i < _f.length; _i++) {
                    var arrItem = _f[_i];
                    arr.push(recursiveHashMapStringify(arrItem));
                }
                return assign(acc, (_a = {}, _a[cleanKey] = arr, _a));
            }
            if (obj[cleanKey] instanceof javelin_exports_1.ZonedDateTime) {
                return assign(acc, (_b = {}, _b[cleanKey] = obj[cleanKey].toISOString(), _b));
            }
            if (obj[cleanKey] instanceof javelin_exports_1.LocalDate) {
                return assign(acc, (_c = {}, _c[cleanKey] = obj[cleanKey].toISOString(), _c));
            }
            // 5.c. If obj[cleanKey] is an object with the getObject function, run
            //      recursiveHashMapStringify on it, and return
            if (obj[cleanKey] &&
                obj[cleanKey].getObject &&
                typeof obj[cleanKey].getObject === 'function') {
                return assign(acc, (_d = {}, _d[cleanKey] = recursiveHashMapStringify(obj[cleanKey]), _d));
            }
            // 5.d. If no other condition has been met, add the current
            //      key-value (cleanKey) pair to the output object
            return assign(acc, (_e = {}, _e[cleanKey] = obj[cleanKey], _e));
        }, {});
    }
};
/********************************************* EXPORT *********************************************/
/**
 * Json methods to be implemented by the client
 * @author Andrew Faulkner & Julien Guerinet
 * @since 2.3.110
 */
var JavelinJson = /** @class */ (function () {
    function JavelinJson(storeIn) {
        if (storeIn === void 0) { storeIn = store_1.store; }
        /**
         * @param <I>       Model class (hydrated return type)
         * @param json      Json String to parse
         * @param type      Desired model class
         * @param elementId Id of element within the JSON string to parse, null if it's the entire JSON
         * @return Parsed model, null if an error occurred
         */
        this.fromJson = function (json, type, elementId) {
            // Parse JSON string
            var parsedObj;
            try {
                parsedObj = JSON.parse(json);
            }
            catch (_a) {
                if (typeof json === 'string')
                    parsedObj = json;
                else
                    throw new Error("JavelinJson#fromJson cannot parse non-strings. Value: " + json);
            }
            var klass = type.jClass;
            /* Create DateModifier objects at any location in the object requiring it */
            // If klass is DateModifier, ensure DateModifier instance is returned
            if (klass === javelin_exports_1.DateModifier) {
                var years = parsedObj.years, months = parsedObj.months, weeks = parsedObj.weeks, days = parsedObj.days;
                var dm = new javelin_exports_1.DateModifier(years, months, weeks, days);
                return elementId ? dm[elementId] : dm;
                // If klass is Rule$IdDate, ensure .interval prop value returned as DateModifier instance
            }
            else if (klass.name === 'Rule$IdDate') {
                var rule = parsedObj;
                var _b = parsedObj.interval, years = _b.years, months = _b.months, weeks = _b.weeks, days = _b.days;
                rule.interval = new javelin_exports_1.DateModifier(years, months, weeks, days);
                return elementId ? rule[elementId] : rule;
            }
            // Element data of interest can be found at (whole object if none specified)
            var parsedElement = elementId ? parsedObj[elementId] : parsedObj;
            if (typeof parsedElement === 'object' && parsedElement !== null) {
                log.info('JavelinJson # fromJson - parsedElement (object):', parsedElement);
            }
            // If klass is Record, return a record instance (RecordInstance)
            if (klass === javelin_exports_1.Record) {
                var record = javelin_exports_1.Record.newFromDict(parsedElement);
                log.verbose("JavelinJson#fromJson :: returning hydrated record:", record);
                return record;
                // If klass is not a Record, return the parsed element as-is
            }
            else {
                return parsedElement;
            }
        };
        /**
         * @return Parsed list of objects of the given [type], parsed from the given [json] at the given
         *  [elementId] (null if it's the whole json to parse). An empty list if there was an error
         */
        this.listFromJson = function (json, type, elementId) {
            var parsedJson = typeof json === 'string' ? JSON.parse(json) : json;
            var parsedJsonClean = parsedJson && elementId ? parsedJson[elementId] : parsedJson;
            var cleanArr = Array.isArray(parsedJsonClean) ? parsedJsonClean : [parsedJsonClean];
            var klass = type.jClass;
            if (klass === javelin_exports_1.DateModifier) {
                var dateModifiers = cleanArr.map(function (obj) {
                    var dateModifier = new javelin_exports_1.DateModifier(obj.years, obj.months, obj.weeks, obj.days);
                    if (elementId)
                        return dateModifier[elementId];
                    return dateModifier;
                });
                log.verbose("listFromJson: dateModifiers:", dateModifiers);
                return dateModifiers;
            }
            return cleanArr;
        };
        /**
         * @param object Object to convert to a Json String
         * @return Json String
         */
        this.toJson = function (obj) {
            log.info("JavelinJson # toJson - about to recursiveHashMapStringify obj:", obj);
            try {
                var json = JSON.stringify(recursiveHashMapStringify(obj));
                log.info('JavelinJson # toJson - json output:', json);
                return json;
            }
            catch (error) {
                // prettier-ignore
                log.error("JavelinJson#toJson :: failed to parse & stringify given value (obj):", obj, "with error:", error);
                throw error;
            }
        };
        this.store = storeIn;
    }
    return JavelinJson;
}());
exports.JavelinJson = JavelinJson;
//# sourceMappingURL=javelin-json.js.map